import { cn } from './ui/utils';

/**
 * SecondarySidebar — Light grey contextual navigation panel.
 * 
 * Creates a unified canvas hierarchy:
 * White (global nav) → Light Grey (context nav + canvas) → White (content cards)
 * 
 * Uses the same grey as the background canvas so the sidebar
 * feels "outside" the floating white content cards.
 * Active items use electric purple (#be43ff) highlights.
 */

interface SecondarySidebarProps {
  children: React.ReactNode;
  width?: string;
  className?: string;
}

export function SecondarySidebar({ children, width = 'w-64', className }: SecondarySidebarProps) {
  return (
    <div
      className={cn(
        width,
        'bg-[#f8f9fa] flex flex-col shrink-0 relative overflow-hidden h-full min-h-0',
        className
      )}
    >
      {/* Content */}
      <div className="relative flex flex-col flex-1 overflow-hidden min-h-0">
        {children}
      </div>
    </div>
  );
}

/* ─── Sub-components for consistent light-surface styling ─── */

interface SidebarHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export function SecondarySidebarHeader({ children, className }: SidebarHeaderProps) {
  return (
    <div className={cn('p-4 border-b border-gray-200/70', className)}>
      {children}
    </div>
  );
}

interface SidebarSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  icon?: React.ReactNode;
  clearSearchAriaLabel?: string;
}

export function SecondarySidebarSearch({
  value,
  onChange,
  placeholder = 'Search...',
  className,
  icon,
  clearSearchAriaLabel = 'Clear search',
}: SidebarSearchProps) {
  return (
    <div className={cn('relative', className)}>
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
        {icon || (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        )}
      </div>
      {value.trim().length > 0 && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          aria-label={clearSearchAriaLabel}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-9 pr-9 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-[#be43ff]/50 focus:border-[#be43ff]/40 transition-colors"
      />
    </div>
  );
}

interface SidebarNavItemProps {
  children: React.ReactNode;
  isActive?: boolean;
  onClick?: () => void;
  icon?: React.ReactNode;
  className?: string;
  badge?: React.ReactNode;
}

export function SecondarySidebarNavItem({ children, isActive, onClick, icon, className, badge }: SidebarNavItemProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left group',
        isActive
          ? 'bg-[#be43ff]/10 text-[#be43ff]'
          : 'text-gray-600 hover:bg-gray-200/60 hover:text-gray-900',
        className
      )}
    >
      {icon && (
        <span className={cn(
          'shrink-0 transition-colors',
          isActive ? 'text-[#be43ff]' : 'text-gray-400 group-hover:text-gray-600'
        )}>
          {icon}
        </span>
      )}
      <span className="flex-1 min-w-0 truncate text-sm">{children}</span>
      {badge && <span className="shrink-0">{badge}</span>}
    </button>
  );
}

interface SidebarSectionLabelProps {
  children: React.ReactNode;
  className?: string;
}

export function SecondarySidebarSectionLabel({ children, className }: SidebarSectionLabelProps) {
  return (
    <h3 className={cn('text-[11px] text-gray-400 uppercase tracking-widest px-3 mb-2', className)}>
      {children}
    </h3>
  );
}

interface SidebarDividerProps {
  className?: string;
}

export function SecondarySidebarDivider({ className }: SidebarDividerProps) {
  return <div className={cn('border-t border-gray-200/70 my-2', className)} />;
}
